import {
  useRef,
  useState,
  useEffect,
  useMemo,
  createRef,
  RefObject,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import debounce from 'lodash/debounce';
import {
  Dropdown,
  tailwindOverride,
  Tag,
  Tooltip,
  FormItem,
  Checkbox,
  Button,
} from '@getsynapse/design-system';
import { Option, Request } from 'utils/customTypes';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import {
  getRequests,
  requestsSelectors,
  setSearchParameter,
  setIsFetchingNext,
} from 'state/normalized/requests/requestsSlice';
import { EDITABLE_REQUEST_STATUSES } from 'utils/constants/request';
import { updateProjectLinkedRequests } from 'state/Project/projectSlice';

export type RequestsDropdownProps = {
  linkedRequests: Request[];
  onClose: () => void;
  projectId: string;
};

const mapRequestToOption = (request: Request): Option => ({
  value: request.id,
  label: request.title!,
});

const RequestsDropdown = ({
  onClose,
  linkedRequests,
  projectId,
}: RequestsDropdownProps) => {
  const dispatch = useDispatch();

  const [truncatedItems, setTruncatedItems] = useState<boolean[]>([]);
  const [dropdownClicks, setDropdownClicks] = useState(0);
  const [updateTruncatedList, setUpdateTruncatedList] = useState(false);
  const [allSelectedRequests, setAllSelectedRequests] = useState<Option[]>([]);

  const requestsRefs = useRef<RefObject<HTMLLIElement>[]>([]);

  const requests = useSelector(requestsSelectors.selectAll);
  const canFetchMore = useSelector(requestsSelectors.selectCanFetchMore);
  const searchParameter = useSelector(requestsSelectors.selectSearchParameter);

  const dropdownOptions = useMemo(
    () => requests.map(mapRequestToOption),
    [requests]
  );

  const originalLinkedRequests = useMemo(
    () => linkedRequests.map(mapRequestToOption),
    [linkedRequests]
  );

  const newSelectedRequests = useMemo(
    () =>
      allSelectedRequests.filter(
        (request) =>
          !originalLinkedRequests.some(
            (originalRequest) => originalRequest.value === request.value
          )
      ),
    [allSelectedRequests, originalLinkedRequests]
  );

  useEffect(() => {
    setAllSelectedRequests(originalLinkedRequests as Option[]);
  }, [originalLinkedRequests]);

  useEffect(() => {
    const fetchRequests = async () => {
      await dispatch(
        getRequests({
          status: EDITABLE_REQUEST_STATUSES,
          pageSize: 30,
        })
      );
      setUpdateTruncatedList(true);
    };

    fetchRequests();
  }, [dispatch]);

  useEffect(() => {
    if (dropdownClicks === 1) {
      setTruncatedItems([]);
    }
  }, [dropdownClicks]);

  useEffect(() => {
    requestsRefs.current = dropdownOptions.map(() =>
      createRef<HTMLLIElement>()
    );
    setTruncatedItems([]);
  }, [dropdownOptions]);

  useEffect(() => {
    if (!updateTruncatedList) return;

    const frameId = requestAnimationFrame(() => {
      const truncationStates = requestsRefs.current.map((ref) => {
        const item = ref.current;
        if (!item) return false;
        return item.scrollWidth! > item.clientWidth!;
      });

      setTruncatedItems(truncationStates);
      setUpdateTruncatedList(false);
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [updateTruncatedList]);

  const onSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        if (value === searchParameter) return;

        dispatch(setSearchParameter(value));
        dispatch(
          getRequests({
            status: EDITABLE_REQUEST_STATUSES,
            pageSize: 30,
          })
        );
        setUpdateTruncatedList(true);
      }, 300),
    [dispatch, searchParameter]
  );

  useEffect(() => {
    return () => {
      onSearchChange.cancel();
    };
  }, [onSearchChange]);

  const renderOption = useCallback(
    (
      option: Option,
      selected: boolean,
      selectOption: (option: Option) => void,
      { className, ...otherProps }
    ) => {
      const index = dropdownOptions.findIndex(
        (opt) => opt.value === option.value
      );
      const isCurrentItemTruncated =
        index >= 0 ? !!truncatedItems[index] : false;

      const isOptionSelected = originalLinkedRequests?.some(
        (selectedOption) => selectedOption.value === option.value
      );

      const getClassNames = () =>
        tailwindOverride(
          className,
          selected
            ? 'hover:bg-primary focus-visible:bg-primary'
            : 'hover:bg-secondary-lightest hover:shadow-list-item-hover-green focus-visible:bg-secondary-lightest focus-visible:shadow-list-item-hover-green',
          {
            'cursor-not-allowed': isOptionSelected,
          }
        );

      const handleClick = () => {
        if (!isOptionSelected) {
          selectOption(option);
        }
      };

      const checkboxId = `requests-dropdown-checkbox-${option.value}`;

      const optionItem = (
        <li
          {...otherProps}
          ref={index >= 0 ? requestsRefs.current[index] : undefined}
          className={tailwindOverride(
            'flex justify-between	group',
            getClassNames()
          )}
          onClick={handleClick}
        >
          <Checkbox
            disabled={isOptionSelected}
            checked={isOptionSelected || selected}
            label={option.label}
            id={checkboxId}
            inputProps={{
              className: tailwindOverride('mt-0', {
                'group-hover:border-neutral-white group-focus-visible:border-neutral-white':
                  selected && !isOptionSelected,
                'group-hover:border-secondary-dark group-focus-visible:border-secondary-dark':
                  !selected && !isOptionSelected,
              }),
            }}
            className={tailwindOverride('inline-block', {
              'group-hover:text-secondary-darker group-focus-visible:text-secondary-darker':
                !selected && !isOptionSelected,
              'group-hover:text-neutral-white group-focus-visible:text-neutral-white':
                selected && !isOptionSelected,
              truncate: isCurrentItemTruncated,
            })}
          />
        </li>
      );

      if (isCurrentItemTruncated) {
        return (
          <Tooltip
            className='w-full'
            ariaId='dropdown-tooltip'
            openMode='hover1'
            timeout={0}
            trigger={optionItem}
            usePortal
            showPopper
            contentProps={{ className: 'z-10 text-sm' }}
          >
            {option.label}
          </Tooltip>
        );
      }

      if (isOptionSelected && truncatedItems.length) {
        return (
          <Tooltip
            className='w-full'
            ariaId='dropdown-tooltip'
            openMode='hover1'
            timeout={0}
            trigger={optionItem}
            usePortal
            showPopper
            contentProps={{ className: 'z-10 text-sm' }}
          >
            {intl.get(
              'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.REQUEST_ALREADY_LINKED'
            )}
          </Tooltip>
        );
      }

      return optionItem;
    },
    [dropdownOptions, truncatedItems, originalLinkedRequests]
  );

  const loadMoreOptions = useCallback(async () => {
    if (!canFetchMore) return;

    dispatch(setIsFetchingNext(true));
    try {
      await dispatch(
        getRequests({
          status: EDITABLE_REQUEST_STATUSES,
          pageSize: 30,
          fetchNext: true,
        })
      );
      setUpdateTruncatedList(true);
    } finally {
      dispatch(setIsFetchingNext(false));
    }
  }, [dispatch, canFetchMore]);

  const handleDropdownClick = () => {
    if (dropdownClicks === 0) {
      setUpdateTruncatedList(true);
    }
    setDropdownClicks((prevState) => prevState + 1);
  };

  const handleLinkRequestsToProject = async () => {
    const selectedRequestIds = allSelectedRequests.map(
      (option) => option.value
    );
    const originalRequestIds = originalLinkedRequests.map(
      (option) => option.value
    );

    const requestIds = Array.from(
      new Set([...selectedRequestIds, ...originalRequestIds])
    );

    onClose();

    try {
      await dispatch(
        updateProjectLinkedRequests({
          projectId,
          requestIds,
        })
      );

      dispatch(
        showSnackbarNotification({
          notificationVariant: 'success',
          notificationTitle:
            newSelectedRequests.length === 1
              ? intl.get(
                  'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.SINGLE_LINK_SUCCESS_MESSAGE'
                )
              : intl.get(
                  'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.MULTIPLE_LINK_SUCCESS_MESSAGE',
                  { numberOfRequests: newSelectedRequests.length }
                ),
        })
      );
    } catch {
      dispatch(
        showSnackbarNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.LINK_ERROR_TITLE'
          ),
          notificationMessage: intl.get(
            'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.ERROR_MESSAGE_RELOAD'
          ),
          autoHide: false,
        })
      );
    }
  };

  return (
    <div className='flex w-full items-end'>
      <FormItem
        label={intl.get('PROJECT_DETAIL.LINKED_REQUESTS_SECTION.LINK_REQUEST')}
        className='w-full mr-6'
      >
        <Dropdown
          values={newSelectedRequests}
          onClick={handleDropdownClick}
          onChange={setAllSelectedRequests}
          options={dropdownOptions}
          filterable
          multiple
          triggerProps={{
            placeholder: intl.get(
              'PROJECT_DETAIL.LINKED_REQUESTS_SECTION.PLACEHOLDER'
            ),
            'data-testid': 'requests-dropdown-trigger',
          }}
          listProps={{
            'data-testid': 'requests-dropdown-list',
          }}
          renderOption={renderOption}
          onFilterChange={onSearchChange}
          renderSelectedOptionTag={(
            { textClassName, ...restOfProps },
            option
          ) => {
            const isOptionSelected = newSelectedRequests?.some(
              (selectedOption) =>
                selectedOption.value === (option as Option).value
            );

            if (!isOptionSelected) return null;

            return (
              <Tag
                key={(option as Option).value}
                textClassName='text-secondary-dark'
                {...restOfProps}
              />
            );
          }}
          enableLazyLoading
          loadMoreOptions={loadMoreOptions}
          onClose={() => {
            if (searchParameter) {
              dispatch(setSearchParameter(''));
            }
            setDropdownClicks(0);
          }}
          filterOptions={() => dropdownOptions}
        />
      </FormItem>
      <div className='flex'>
        <Button
          className='mr-2 h-10'
          disabled={newSelectedRequests.length === 0}
          onClick={handleLinkRequestsToProject}
          data-testid='link-requests-button'
        >
          {intl.get('LINK')}
        </Button>
        <Button
          className='h-10'
          onClick={onClose}
          variant='tertiary'
          data-testid='cancel-link-requests-button'
        >
          {intl.get('CANCEL')}
        </Button>
      </div>
    </div>
  );
};

export default RequestsDropdown;
